import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserGraduate, FaChild } from "react-icons/fa";
import "../styles/userlevelCategorySelection.css";

const levels = [
  { name: "Beginner", icon: <FaChild />, path: "/beginner-sizing" },
  { name: "Expert", icon: <FaUserGraduate />, path: "/calculator/sizing" },
];

export default function UserLevelSelection() {
  const navigate = useNavigate();
  const cardRefs = useRef([]);
  const timeoutsRef = useRef([]);
  const intervalRef = useRef(null);
  const [pointerStyle, setPointerStyle] = useState({
    left: -100,
    top: -100,
    opacity: 0,
  });
  const [activeIndex, setActiveIndex] = useState(null);

  // 🔒 Prevent page scrolling while this component is mounted
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const moveToCard = (index) => {
    const el = cardRefs.current[index];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = Math.round(window.scrollX + rect.left + 10);
    const top = Math.round(window.scrollY + rect.top + rect.height * 0.5 - 12);
    setPointerStyle({ left, top, opacity: 1 });
    setActiveIndex(index);
  };

  useEffect(() => {
    const CARD_HOLD_MS = 900;
    const REPEAT_INTERVAL_MS = 10000;

    function clearAllTimeouts() {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
    }

    function runSequence() {
      clearAllTimeouts();
      const count = levels.length;
      if (!cardRefs.current[0]) {
        const retry = setTimeout(runSequence, 200);
        timeoutsRef.current.push(retry);
        return;
      }
      for (let i = 0; i < count; i++) {
        const t = setTimeout(() => {
          moveToCard(i);
        }, i * CARD_HOLD_MS);
        timeoutsRef.current.push(t);
      }
      const hideT = setTimeout(() => {
        setPointerStyle((s) => ({ ...s, opacity: 0 }));
        setActiveIndex(null);
      }, count * CARD_HOLD_MS + 250);
      timeoutsRef.current.push(hideT);
    }

    const startup = setTimeout(() => {
      runSequence();
      intervalRef.current = setInterval(runSequence, REPEAT_INTERVAL_MS);
    }, 200);
    timeoutsRef.current.push(startup);

    return () => {
      clearAllTimeouts();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <>
      <style>{`
        .floating-pointer {
          position: fixed;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          z-index: 9999;
          transition: left 260ms ease, top 260ms ease, opacity 180ms ease;
          pointer-events: none;
          will-change: left, top, opacity, transform;
          opacity: 0;
        }
        .floating-pointer.visible {
          opacity: 1;
          animation: fp-bounce 0.6s infinite alternate;
        }
        @keyframes fp-bounce {
          from { transform: translateY(0); }
          to { transform: translateY(-6px); }
        }
        .category-card {
          position: relative;
          z-index: 1;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .category-card.pointed {
          transform: scale(1.05);
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
        }
        .category-grid {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 50px;
          margin-top: 40px;
        }
      `}</style>

      {/* Floating Pointer */}
      <div
        className={`floating-pointer ${pointerStyle.opacity > 0 ? "visible" : ""}`}
        style={{
          left: `${pointerStyle.left}px`,
          top: `${pointerStyle.top}px`,
          opacity: pointerStyle.opacity,
        }}
        aria-hidden="true"
      >
        👉
      </div>

      <div className="category-page" style={{ height: "100vh", overflow: "hidden" }}>
        <div className="category-container">
          <h1 className="category-title">Select Your Experience Level</h1>
          <p className="category-subtitle">
            Choose Beginner if you’re a homeowner, or individual without solar technical knowledge,
            or Expert if you’re an installer, manufacturer, or vendor with solar technical experience.
          </p>

          <div className="category-grid">
            {levels.map((level, idx) => (
              <div
                key={level.name}
                className={`category-card ${activeIndex === idx ? "pointed" : ""}`}
                onClick={() => navigate(level.path)}
                ref={(el) => (cardRefs.current[idx] = el)}
              >
                <div className="category-icon">{level.icon}</div>
                <h3>{level.name}</h3>
              </div>
            ))}
          </div>
        </div>

        <div className="bottom-section">
          <div className="privacy-terms">
            
            <h4>
              &copy; 2025 Globisun Energies - All rights reserved <br />
              Powered by{" "}
              <a
                href="https://globalnetexperts.com.ng/"
                target="_blank"
                rel="noreferrer"
              >
                GlobalNet ICT Solutions
              </a>
            </h4>
          </div>
        </div>
      </div>
    </>
  );
}
